/** Melody transcribed from Mysid's CC0 score in F major, transposed down 5 semitones.
 * See docs/ORBIT_MUSIC_BOX.md for the inspected source and measure-by-measure audit. */
export type InstrumentId = "marimba" | "bell" | "bass" | "percussion";
export type Pitch = `${"C" | "D" | "E" | "F" | "G" | "A" | "B"}${number}`;
export type NoteEvent = Readonly<{
  id: string;
  startBeat: number;
  pitch: Pitch;
  durationBeats: number;
  velocity: number;
  instrumentId: InstrumentId;
  circleId: InstrumentId;
  triggerAngle: number;
  measure: number;
  articulation: "normal" | "wood" | "shaker";
}>;
export const SONG = {
  bpm: 120,
  beatsPerBar: 4,
  leadIn: 0.25,
  totalBeats: 64,
  tail: 1.75,
} as const;
export const DURATION =
  SONG.leadIn + (SONG.totalBeats * 60) / SONG.bpm + SONG.tail;
export const ENTRIES: Record<InstrumentId, number> = {
  marimba: 0,
  bell: 8,
  bass: 16,
  percussion: 24,
};
export const MELODY: readonly (readonly [Pitch, number])[] = [
  ["C4", 1],
  ["D4", 1],
  ["E4", 1],
  ["C4", 1],
  ["C4", 1],
  ["D4", 1],
  ["E4", 1],
  ["C4", 1],
  ["E4", 1],
  ["F4", 1],
  ["G4", 2],
  ["E4", 1],
  ["F4", 1],
  ["G4", 2],
  ["G4", 0.5],
  ["A4", 0.5],
  ["G4", 0.5],
  ["F4", 0.5],
  ["E4", 1],
  ["C4", 1],
  ["G4", 0.5],
  ["A4", 0.5],
  ["G4", 0.5],
  ["F4", 0.5],
  ["E4", 1],
  ["C4", 1],
  ["C4", 1],
  ["G3", 1],
  ["C4", 2],
  ["C4", 1],
  ["G3", 1],
  ["C4", 2],
];
const notes: NoteEvent[] = [];
const add = (
  instrumentId: InstrumentId,
  startBeat: number,
  pitch: Pitch,
  durationBeats: number,
  velocity: number,
  articulation: NoteEvent["articulation"] = "normal",
) => {
  notes.push({
    id: `${instrumentId}-${notes.length}`,
    instrumentId,
    circleId: instrumentId,
    startBeat,
    pitch,
    durationBeats,
    velocity,
    articulation,
    measure: Math.floor(startBeat / SONG.beatsPerBar),
    triggerAngle: -Math.PI / 2,
  });
};
for (const offset of [0, 32]) {
  let beat = offset;
  for (const [pitch, duration] of MELODY) {
    add("marimba", beat, pitch, duration, beat % 4 === 0 ? 0.86 : 0.73);
    beat += duration;
  }
}
// A two-bar canon; the last two bars deliberately reunite for a tonic cadence.
for (const offset of [8, 40]) {
  let beat = offset;
  for (const [pitch, duration] of MELODY) {
    if (beat >= 56) break;
    add(
      "bell",
      beat,
      `${pitch[0]}${Number(pitch.slice(1)) + 1}` as Pitch,
      duration,
      0.52,
    );
    beat += duration;
  }
}
for (const offset of [56, 60]) {
  add("bell", offset, "C5", 1, 0.48);
  add("bell", offset + 1, "G4", 1, 0.42);
  add("bell", offset + 2, "C5", 2, 0.54);
}
// Gentle tonic/fifth ostinato supports the traditional tonic-pedal round.
for (let beat = 16; beat < 64; beat += 4) {
  add("bass", beat, "C3", 1.8, 0.7);
  add("bass", beat + 2, beat === 60 ? "C3" : "G2", 1.8, 0.55);
}
for (let beat = 24; beat < 62; beat += 0.5) {
  add(
    "percussion",
    beat,
    "C5",
    0.18,
    beat % 2 === 0 ? 0.45 : 0.22,
    beat % 2 === 0 ? "wood" : "shaker",
  );
}
export const NOTES: readonly NoteEvent[] = notes.sort(
  (a, b) => a.startBeat - b.startBeat || a.id.localeCompare(b.id),
);
